import { Key, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDate } from 'date-fns';
import { StageTag } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { RFQ } from 'src/shared/types/rfq';

import {
  ButtonV2,
  cn,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography,
  useDisclosure
} from 'src/shared/components/ui';

import incompleteProductColumns from '../constants/incompleteProductColumns';
import { useArchiveProductDraft } from '../hooks/useArchiveProductDraft';
import {
  getIncompleteProductLastUpdatedAt,
  sortByLastUpdated
} from '../utils/lastUpdated';
import ProductNameCell from './ProductNameCell';

interface IncompleteProductsTableProps {
  isLoading?: boolean;
  /** Unpublished RFQs, i.e. products the user started but never finished. */
  products?: RFQ[];
  isSuccess?: boolean;
}

const IncompleteProductsTable: React.FC<IncompleteProductsTableProps> = ({
  isLoading,
  products,
  isSuccess
}) => {
  const { mutate: archiveDraft, isPending } = useArchiveProductDraft();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [pendingId, setPendingId] = useState<number | null>(null);

  const handleDeletePress = (id: number) => {
    setPendingId(id);
    onOpen();
  };

  const handleConfirmDelete = (onClose: () => void) => {
    if (pendingId !== null) {
      archiveDraft(
        { id: pendingId, archived: true },
        {
          onSettled: () => {
            setPendingId(null);
            onClose();
          }
        }
      );
    }
  };

  const renderCell = useCallback(
    (item: RFQ, columnKey: Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <ProductNameCell
              name={item.document.name}
              image={item.image}
              id={item.id.toString()}
            />
          );

        case 'stage':
          return <StageTag type="draft" />;

        case 'updated': {
          const updatedAt = getIncompleteProductLastUpdatedAt(item);

          return (
            <Typography variant="body-1" weight="medium">
              {updatedAt ? formatDate(updatedAt, 'MMM dd, yyyy') : '-'}
            </Typography>
          );
        }

        case 'actions':
          return (
            <ButtonV2
              size="sm"
              color="tertiary"
              className="w-20"
              onPress={() => handleDeletePress(item.id)}
            >
              Delete
            </ButtonV2>
          );

        default:
          return '';
      }
    },
    // handleDeletePress is stable across renders (uses state setter + onOpen, both stable refs)

    []
  );

  const sortedProducts = useMemo(() => {
    if (!products) {
      return [];
    }

    return sortByLastUpdated(products, getIncompleteProductLastUpdatedAt);
  }, [products]);

  const count = products?.length ?? 0;
  const title = count > 0 ? `${count} Incomplete` : 'No incomplete products';

  return (
    <>
      <div className="flex flex-col">
        <Typography variant="h4" as="h2" weight="bold">
          {isSuccess && <>{title}</>}
        </Typography>

        <Table
          aria-label="Incomplete products table"
          className={cn('heroui-table', count === 0 && 'pointer-events-none')}
        >
          <TableHeader columns={incompleteProductColumns}>
            {(column) => (
              <TableColumn className="px-4" key={column.key}>
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            items={sortedProducts}
            emptyContent={
              isLoading ? (
                <Spinner />
              ) : (
                "Products you start but don't finish show up here"
              )
            }
          >
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>
                    {columnKey === 'actions' ? (
                      <div className="h-16 flex items-center">
                        {renderCell(item, columnKey)}
                      </div>
                    ) : (
                      <Link
                        className="h-16 flex items-center"
                        to={`${ROUTE_PATHS.BUILD_ANYTHING}?rfq=${item.id}&step=${item.builder_step || 'playground'}`}
                      >
                        <div className="truncate">
                          {renderCell(item, columnKey)}
                        </div>
                      </Link>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="p-5 border-b border-gray-4">
                Delete draft?
              </ModalHeader>
              <ModalBody className="px-5 py-4 !flex-initial">
                <Typography variant="body-1">
                  Are you sure you want to delete this draft?
                </Typography>
              </ModalBody>
              <ModalFooter className="border-t border-gray-4 justify-end">
                <div className="flex gap-2 items-center">
                  {isPending && <Spinner size="sm" />}
                  <ButtonV2
                    color="tertiary"
                    onPress={onClose}
                    isDisabled={isPending}
                  >
                    Cancel
                  </ButtonV2>
                  <ButtonV2
                    color="danger"
                    isLoading={isPending}
                    isDisabled={isPending}
                    onPress={() => handleConfirmDelete(onClose)}
                  >
                    Delete
                  </ButtonV2>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default IncompleteProductsTable;
